'use client';

import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { Input } from '../../components/input';
import { IconButton } from '../../components/icon-button';
import { Button } from '../../components/button';
import { DatePicker } from '../../components/date-picker';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import {
  Calendar01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { parseDate } from '@internationalized/date';

export type DateRangeFilterSize = 'sm' | 'md';
export interface DateRangeFilterProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  size?: DateRangeFilterSize;
  label?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  disabled?: boolean;
  maxSelectableDate?: Date | null;
}
export function DateRangeFilter({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  size = 'md',
  label,
  startPlaceholder,
  endPlaceholder,
  disabled = false,
  maxSelectableDate = null,
}: DateRangeFilterProps) {
  const t = useTranslations('designSystem.dateRangeFilter');
  const locale = useLocale();
  const displayLabel = label || t('label');
  const displayStartPlaceholder = startPlaceholder || t('startPlaceholder');
  const displayEndPlaceholder = endPlaceholder || t('endPlaceholder');
  // Size mappings
  const inputSize = size === 'sm' ? 'xs' : 'sm';
  const inputHeight = size === 'sm' ? '6' : undefined;
  const buttonSize = size === 'sm' ? 'xs' : 'sm';
  const iconSize = size === 'sm' ? 12 : 16;
  const buttonHeight = size === 'sm' ? '6' : undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [componentKey, setComponentKey] = useState(0);
  const prevStartDate = useRef(startDate);
  const prevEndDate = useRef(endDate);

  // Detect when both dates become null from outside (global clear)
  useEffect(() => {
    const wasCleared =
      (prevStartDate.current !== null || prevEndDate.current !== null) &&
      startDate === null &&
      endDate === null;

    if (wasCleared) {
      setComponentKey((prev) => prev + 1);
      setIsOpen(false);
    }

    prevStartDate.current = startDate;
    prevEndDate.current = endDate;
  }, [startDate, endDate]);
  const handleClear = useCallback(() => {
    onStartDateChange(null);
    onEndDateChange(null);
    // Force complete remount only when clearing
    setComponentKey((prev) => prev + 1);
    setIsOpen(false);
  }, [onStartDateChange, onEndDateChange]);
  const hasValue = startDate || endDate;
  const handleRangeChange = useCallback<
    NonNullable<DatePicker.RootProps['onValueChange']>
  >(
    (details) => {
      const [startString, endString] = details.valueAsString;

      if (startString) {
        onStartDateChange(new Date(startString));
      } else {
        onStartDateChange(null);
      }

      if (endString) {
        onEndDateChange(new Date(endString));
      } else {
        onEndDateChange(null);
      }
    },
    [onStartDateChange, onEndDateChange],
  );
  const ContainerStack = size === 'sm' ? HStack : VStack;
  const containerGap = size === 'sm' ? 2 : 2;
  // Convert Date objects to DateValue format for controlled DatePicker
  // Only provide initial value, don't control it (ark-ui limitation)
  const maxDateValue = useMemo(() => {
    const ceiling = maxSelectableDate ?? new Date();
    const year = ceiling.getFullYear();
    const month = String(ceiling.getMonth() + 1).padStart(2, '0');
    const day = String(ceiling.getDate()).padStart(2, '0');

    return parseDate(`${year}-${month}-${day}`);
  }, [maxSelectableDate]);

  const isDateUnavailable = useCallback<
    NonNullable<DatePicker.RootProps['isDateUnavailable']>
  >((value, _details) => value.compare(maxDateValue) > 0, [maxDateValue]);

  const defaultValue = useMemo(() => {
    const values = [];

    if (startDate instanceof Date && !isNaN(startDate.getTime())) {
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      const day = String(startDate.getDate()).padStart(2, '0');

      values.push(parseDate(`${year}-${month}-${day}`));
    }

    if (endDate instanceof Date && !isNaN(endDate.getTime())) {
      const year = endDate.getFullYear();
      const month = String(endDate.getMonth() + 1).padStart(2, '0');
      const day = String(endDate.getDate()).padStart(2, '0');

      values.push(parseDate(`${year}-${month}-${day}`));
    }

    return values.length > 0 ? values : undefined;
  }, [startDate, endDate]);

  return (
    <ContainerStack
      gap={containerGap}
      alignItems={size === 'sm' ? 'center' : 'flex-start'}
    >
      <Text
        as="span"
        fontSize={size === 'sm' ? 'xs' : 'sm'}
        whiteSpace="nowrap"
        color="fg.default"
      >
        {displayLabel}
      </Text>
      <HStack gap={size === 'sm' ? 1 : 2} alignItems="center">
        {/* Date Range Picker */}
        <DatePicker.Root
          key={componentKey}
          positioning={{ strategy: 'fixed', sameWidth: true }}
          startOfWeek={1}
          selectionMode="range"
          defaultValue={defaultValue}
          onValueChange={handleRangeChange}
          open={isOpen}
          onOpenChange={(details) => setIsOpen(details.open)}
          locale={locale}
          disabled={disabled}
          max={maxDateValue}
          isDateUnavailable={isDateUnavailable}
        >
          <DatePicker.Control display="flex" gap={size === 'sm' ? 1 : 2}>
            <DatePicker.Input index={0} asChild>
              <Input
                size={inputSize}
                h={inputHeight}
                placeholder={displayStartPlaceholder}
                minW={size === 'sm' ? '100px' : '140px'}
                w={size === 'sm' ? '110px' : '150px'}
                flex="0 0 auto"
              />
            </DatePicker.Input>
            <DatePicker.Input index={1} asChild>
              <Input
                size={inputSize}
                h={inputHeight}
                placeholder={displayEndPlaceholder}
                minW={size === 'sm' ? '100px' : '140px'}
                w={size === 'sm' ? '110px' : '150px'}
                flex="0 0 auto"
              />
            </DatePicker.Input>
            <DatePicker.Trigger asChild>
              <IconButton
                variant="outline"
                size={buttonSize}
                h={buttonHeight}
                aria-label={t('openCalendar')}
              >
                <Calendar01Icon size={iconSize} />
              </IconButton>
            </DatePicker.Trigger>
          </DatePicker.Control>
          <DatePicker.Positioner>
            <DatePicker.Content>
              <DatePicker.View view="day">
                <DatePicker.Context>
                  {(api) => (
                    <>
                      <DatePicker.ViewControl>
                        <DatePicker.PrevTrigger asChild>
                          <IconButton variant="ghost" size="sm">
                            <ArrowLeft01Icon size={16} />
                          </IconButton>
                        </DatePicker.PrevTrigger>
                        <DatePicker.ViewTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <DatePicker.RangeText />
                          </Button>
                        </DatePicker.ViewTrigger>
                        <DatePicker.NextTrigger asChild>
                          <IconButton variant="ghost" size="sm">
                            <ArrowRight01Icon size={16} />
                          </IconButton>
                        </DatePicker.NextTrigger>
                      </DatePicker.ViewControl>
                      <DatePicker.Table>
                        <DatePicker.TableHead>
                          <DatePicker.TableRow>
                            {api.weekDays.map((weekDay, id) => (
                              <DatePicker.TableHeader key={id}>
                                {weekDay.narrow}
                              </DatePicker.TableHeader>
                            ))}
                          </DatePicker.TableRow>
                        </DatePicker.TableHead>
                        <DatePicker.TableBody>
                          {api.weeks.map((week, id) => (
                            <DatePicker.TableRow key={id}>
                              {week.map((day, id) => (
                                <DatePicker.TableCell key={id} value={day}>
                                  <DatePicker.TableCellTrigger asChild>
                                    <IconButton variant="ghost">
                                      {day.day}
                                    </IconButton>
                                  </DatePicker.TableCellTrigger>
                                </DatePicker.TableCell>
                              ))}
                            </DatePicker.TableRow>
                          ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>
              <DatePicker.View view="month">
                <DatePicker.Context>
                  {(api) => (
                    <>
                      <DatePicker.ViewControl>
                        <DatePicker.PrevTrigger asChild>
                          <IconButton variant="ghost" size="sm">
                            <ArrowLeft01Icon size={16} />
                          </IconButton>
                        </DatePicker.PrevTrigger>
                        <DatePicker.ViewTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <DatePicker.RangeText />
                          </Button>
                        </DatePicker.ViewTrigger>
                        <DatePicker.NextTrigger asChild>
                          <IconButton variant="ghost" size="sm">
                            <ArrowRight01Icon size={16} />
                          </IconButton>
                        </DatePicker.NextTrigger>
                      </DatePicker.ViewControl>
                      <DatePicker.Table>
                        <DatePicker.TableBody>
                          {api
                            .getMonthsGrid({ columns: 4, format: 'short' })
                            .map((months, id) => (
                              <DatePicker.TableRow key={id}>
                                {months.map((month, id) => (
                                  <DatePicker.TableCell
                                    key={id}
                                    value={month.value}
                                  >
                                    <DatePicker.TableCellTrigger asChild>
                                      <Button variant="ghost">
                                        {month.label}
                                      </Button>
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                ))}
                              </DatePicker.TableRow>
                            ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>
              <DatePicker.View view="year">
                <DatePicker.Context>
                  {(api) => (
                    <>
                      <DatePicker.ViewControl>
                        <DatePicker.PrevTrigger asChild>
                          <IconButton variant="ghost" size="sm">
                            <ArrowLeft01Icon size={16} />
                          </IconButton>
                        </DatePicker.PrevTrigger>
                        <DatePicker.ViewTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <DatePicker.RangeText />
                          </Button>
                        </DatePicker.ViewTrigger>
                        <DatePicker.NextTrigger asChild>
                          <IconButton variant="ghost" size="sm">
                            <ArrowRight01Icon size={16} />
                          </IconButton>
                        </DatePicker.NextTrigger>
                      </DatePicker.ViewControl>
                      <DatePicker.Table>
                        <DatePicker.TableBody>
                          {api.getYearsGrid({ columns: 4 }).map((years, id) => (
                            <DatePicker.TableRow key={id}>
                              {years.map((year, id) => (
                                <DatePicker.TableCell
                                  key={id}
                                  value={year.value}
                                >
                                  <DatePicker.TableCellTrigger asChild>
                                    <Button variant="ghost">
                                      {year.label}
                                    </Button>
                                  </DatePicker.TableCellTrigger>
                                </DatePicker.TableCell>
                              ))}
                            </DatePicker.TableRow>
                          ))}
                        </DatePicker.TableBody>
                      </DatePicker.Table>
                    </>
                  )}
                </DatePicker.Context>
              </DatePicker.View>
            </DatePicker.Content>
          </DatePicker.Positioner>
        </DatePicker.Root>

        {/* Clear Button */}
        {hasValue && (
          <IconButton
            variant="ghost"
            size={buttonSize}
            h={buttonHeight}
            aria-label={t('clear')}
            onClick={handleClear}
            disabled={disabled}
          >
            <Cancel01Icon size={iconSize} />
          </IconButton>
        )}
      </HStack>
    </ContainerStack>
  );
}
